import React, { useEffect, useMemo, useRef, useState } from 'react';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import {
  ADDON_ITEM_KIND,
  ADDON_PRICING_UNITS,
  OTHER_PRICING_UNIT_OPTIONS,
  getAddonPricingUnit
} from '../../../utils/addonPricing';
import {
  FigmaAddIcon,
  FigmaTrashIcon,
  FigmaAddSquareIcon,
  FigmaCloseModalIcon,
  FigmaCloseTagIcon,
  FigmaChevronDownIcon
} from '../../icons';
import { DEFAULT_BOOKING_HOST_RULES_TEXT } from '../../../utils/bookingHostRules';
import { resolveHostPhotoUrl, isHostPhotoGalleryImageRecord } from '../../../utils/hostPhotoUrl';
import { normalizeTagSlugs, tagsEqual, DEFAULT_CATALOG_ADDON_TAGS } from '../../../utils/catalogTags';
import CatalogTagPicker from './CatalogTagPicker';
import SettingsSpaceScopeBar from './SettingsSpaceScopeBar';
import {
  resolveApiSpaceId,
  filterRowsBySpace,
  filterPackagesBySpace
} from '../utils/venueSpacesUi';

const currencySymbol = '£';

const normalizeOtherItem = (item, fallback = null) => ({
  id: item.id || item.item_id || item.add_on_item_id,
  name: item.name || item.item_name || item.title || fallback?.name || 'Item',
  price: Number(item.price ?? item.price_per_unit ?? item.unit_price ?? fallback?.price ?? 0),
  description: item.description || fallback?.description || '',
  pricing_unit: getAddonPricingUnit(item),
  default_quantity: Number(item.default_quantity ?? item.defaultQuantity ?? fallback?.default_quantity ?? 1),
  tags: normalizeTagSlugs(item.tags ?? fallback?.tags),
});

const normalizeAddOnItem = (item, fallback = null) => ({
  id: item.id || item.item_id || item.add_on_item_id,
  name: item.name || item.item_name || item.title || fallback?.name || 'Item',
  price: Number(item.price ?? item.price_per_unit ?? item.unit_price ?? fallback?.price ?? 0),
  description: item.description || fallback?.description || '',
  tags: normalizeTagSlugs(item.tags ?? fallback?.tags),
});

const normalizePackage = (pkg) => {
  const items =
    (Array.isArray(pkg.items) && pkg.items) ||
    (Array.isArray(pkg.package_items) && pkg.package_items) ||
    (Array.isArray(pkg.packageItems) && pkg.packageItems) ||
    [];

  return {
    id: pkg.id || pkg.package_id,
    name: pkg.name || pkg.package_name || pkg.title || 'Package',
    price: pkg.price != null && !Number.isNaN(Number(pkg.price)) ? Number(pkg.price) : null,
    isActive: pkg.is_active ?? pkg.active ?? true,
    items,
    availableFor: pkg.available_for || pkg.availableFor || {},
    tags: normalizeTagSlugs(pkg.tags),
  };
};

const getItemLabel = (item) => item.name || item.item_name || item.title || 'Item';
const getItemQuantity = (item) => item.quantity ?? item.qty ?? item.count ?? 1;
const getItemPrice = (item) => Number(item.price_per_unit ?? item.price ?? item.unit_price ?? 0);
const toMultilineText = (items = [], pickText) => {
  if (!Array.isArray(items)) return '';
  return items
    .map((item) => pickText(item))
    .map((value) => (value == null ? '' : String(value).trim()))
    .filter(Boolean)
    .join('\n');
};

const getPackageTotalPrice = (pkg) => {
  if (typeof pkg.price === 'number' && !Number.isNaN(pkg.price)) {
    return pkg.price;
  }
  if (!pkg.items || pkg.items.length === 0) {
    return 0;
  }
  return pkg.items.reduce((sum, item) => (
    sum + (getItemPrice(item) * getItemQuantity(item))
  ), 0);
};

const VENUE_TYPES = [
  { value: 'conference_rooms', label: 'Conference Room' },
  { value: 'banquet_halls', label: 'Banquet Hall' },
  { value: 'outdoor_spaces', label: 'Outdoor Space' },
  { value: 'studios', label: 'Studio' },
  { value: 'lofts', label: 'Loft' },
  { value: 'hotels', label: 'Hotel' },
  { value: 'cafes', label: 'Café' },
  { value: 'restaurants', label: 'Restaurant' },
  { value: 'auditoriums', label: 'Auditorium' },
  { value: 'venue', label: 'Venue' }
];

const normalizeVenueImage = (image, fallbackIndex = 0) => {
  const id = image?.id || image?.image_id || image?.uuid || `tmp-${fallbackIndex}`;
  const imageUrl = image?.image_url || image?.url || '';
  return {
    id: String(id),
    imageUrl,
    caption: image?.caption || '',
    isPrimary: Boolean(image?.is_primary),
    uploadStatus: image?.upload_status || '',
    sortOrder: image?.sort_order ?? fallbackIndex
  };
};

const isHostPhotoGalleryImage = (image, hostPhotoUrl) =>
  isHostPhotoGalleryImageRecord(image, hostPhotoUrl);

const isGalleryAuthErrorMessage = (message = '') => {
  const text = String(message || '').toLowerCase();
  return (
    text.includes('not authorised') ||
    text.includes('not authorized') ||
    text.includes('log in again') ||
    text.includes('unauthor') ||
    text.includes('401')
  );
};

const getGalleryAuthMessage = () =>
  'No backend session on this domain. Please sign in again to manage venue photos.';

const toGalleryErrorMessage = (error) => {
  const raw = error?.message || 'Failed to manage venue photos.';
  if (isGalleryAuthErrorMessage(raw)) return getGalleryAuthMessage();
  return raw;
};

const VenueDetailsForm = ({
  venueId,
  venueDetails,
  setVenueDetails,
  addOnItems,
  setAddOnItems,
  otherItems,
  setOtherItems,
  packages,
  setPackages,
  isAdmin = false,
  onSaveVenueDetails,
  onNotify,
  venueSpaces = [],
  settingsSpaceId = null,
  onSettingsSpaceIdChange,
  multiSpace = false
}) => {
  const hasBackendToken = () => Boolean(localStorage.getItem('access_token'));

  const catalogSpaceId = useMemo(
    () => resolveApiSpaceId(settingsSpaceId),
    [settingsSpaceId]
  );
  const catalogSpaceOpts = useMemo(
    () => ({ includeVenueWide: !(multiSpace && catalogSpaceId) }),
    [multiSpace, catalogSpaceId]
  );

  const [activeTab, setActiveTab] = useState('general');
  const [draftAddOnItems, setDraftAddOnItems] = useState([]);
  const [pendingItemDeletes, setPendingItemDeletes] = useState([]);
  const [itemsError, setItemsError] = useState('');
  const [packagesError, setPackagesError] = useState('');
  const [isSavingItem, setIsSavingItem] = useState(false);
  const [isSavingPackage, setIsSavingPackage] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [addOnItemsSnapshot, setAddOnItemsSnapshot] = useState([]);
  const [draftOtherItems, setDraftOtherItems] = useState([]);
  const [pendingOtherDeletes, setPendingOtherDeletes] = useState([]);
  const [otherItemsSnapshot, setOtherItemsSnapshot] = useState([]);
  const prevActiveTabRef = useRef(activeTab);
  const catalogSyncedRef = useRef(null);
  const [venueImages, setVenueImages] = useState([]);
  const [rawVenueGalleryItems, setRawVenueGalleryItems] = useState([]);
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);
  const [replaceAllImages, setReplaceAllImages] = useState(false);
  const [galleryBusy, setGalleryBusy] = useState(false);
  const [galleryError, setGalleryError] = useState('');
  const [selectedHostPhotoFile, setSelectedHostPhotoFile] = useState(null);
  const [hostPhotoBusy, setHostPhotoBusy] = useState(false);
  const [hostPhotoError, setHostPhotoError] = useState('');
  const [hostPhotoCacheBust, setHostPhotoCacheBust] = useState(0);

  const [catalogTags, setCatalogTags] = useState(DEFAULT_CATALOG_ADDON_TAGS);
  const [packageForm, setPackageForm] = useState({
    name: '',
    customPrice: '',
    priceManuallyEdited: false,
    availableFor: {
      tableBooking: true,
      fullVenueHire: false
    },
    items: {},
    tags: [],
  });
  const [showItemsDropdown, setShowItemsDropdown] = useState(false);
  const canManageGallery = Boolean(venueId && hasBackendToken());
  const canManageHostPhoto = Boolean(venueId && setVenueDetails && hasBackendToken());
  const [hostPhotoImageError, setHostPhotoImageError] = useState(false);
  const [hostPhotoOverrideUrl, setHostPhotoOverrideUrl] = useState(null);

  const hostPhotoLocalPreviewUrl = useMemo(() => {
    if (!selectedHostPhotoFile) return null;
    return URL.createObjectURL(selectedHostPhotoFile);
  }, [selectedHostPhotoFile]);

  useEffect(() => {
    return () => {
      if (hostPhotoLocalPreviewUrl) {
        URL.revokeObjectURL(hostPhotoLocalPreviewUrl);
      }
    };
  }, [hostPhotoLocalPreviewUrl]);

  const hostPhotoSavedPreviewUrl = useMemo(() => {
    if (!venueDetails) return null;
    const base = resolveHostPhotoUrl({
      ...venueDetails,
      id: venueId,
      images: rawVenueGalleryItems
    });
    if (!base) return null;
    return hostPhotoCacheBust
      ? `${base}${base.includes('?') ? '&' : '?'}v=${hostPhotoCacheBust}`
      : base;
  }, [venueDetails, venueId, hostPhotoCacheBust, rawVenueGalleryItems]);

  const hostPhotoDisplayUrl =
    hostPhotoOverrideUrl || hostPhotoLocalPreviewUrl || hostPhotoSavedPreviewUrl;

  const hasSavedHostPhoto = Boolean(String(venueDetails?.host_photo_url || '').trim());

  const hostPhotoInitials = useMemo(() => {
    const name = venueDetails?.contact_name || venueDetails?.name || '';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    }
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return 'H';
  }, [venueDetails?.contact_name, venueDetails?.name]);

  useEffect(() => {
    setHostPhotoImageError(false);
  }, [hostPhotoDisplayUrl]);

  const handleHostPhotoFileSelected = (event) => {
    const file = (event.target.files && event.target.files[0]) || null;
    setHostPhotoError('');
    setSelectedHostPhotoFile(file);
    event.target.value = '';
  };

  const handleUploadHostPhoto = async () => {
    if (!venueId || !selectedHostPhotoFile || hostPhotoBusy || !canManageHostPhoto) return;
    try {
      setHostPhotoBusy(true);
      setHostPhotoError('');
      const { filename, imageUrl } = await venueDashboardApiService.uploadHostPhoto(
        venueId,
        selectedHostPhotoFile
      );
      const stored = imageUrl || filename;
      setHostPhotoImageError(false);
      setHostPhotoOverrideUrl(imageUrl || null);
      setVenueDetails?.({ ...venueDetails, host_photo_url: stored });
      setSelectedHostPhotoFile(null);
      if (onSaveVenueDetails) {
        await onSaveVenueDetails({ host_photo_url: stored });
      }
      setHostPhotoOverrideUrl(null);
      setHostPhotoCacheBust(Date.now());
      await refreshVenueImages();
      onNotify?.({
        type: 'success',
        title: 'Host photo uploaded',
        message: 'Your host photo has been saved for this venue.'
      });
    } catch (error) {
      setHostPhotoError(toGalleryErrorMessage(error));
    } finally {
      setHostPhotoBusy(false);
    }
  };

  const handleRemoveHostPhoto = async () => {
    if (!canManageHostPhoto || hostPhotoBusy) return;
    try {
      setHostPhotoBusy(true);
      setHostPhotoError('');
      setSelectedHostPhotoFile(null);
      setHostPhotoOverrideUrl(null);
      setVenueDetails?.({ ...venueDetails, host_photo_url: '' });
      setHostPhotoCacheBust(0);
      if (onSaveVenueDetails) {
        await onSaveVenueDetails({ host_photo_url: '' });
      }
      onNotify?.({
        type: 'success',
        title: 'Host photo removed',
        message: 'The public page will use a placeholder until you upload a new photo.'
      });
    } catch (error) {
      setHostPhotoError(toGalleryErrorMessage(error));
    } finally {
      setHostPhotoBusy(false);
    }
  };

  const hostPhotoUrlKey = venueDetails?.host_photo_url ?? '';

  useEffect(() => {
    const loadVenueImages = async () => {
      if (!venueId) {
        setVenueImages([]);
        return;
      }
      if (!hasBackendToken()) {
        setVenueImages([]);
        setGalleryError(getGalleryAuthMessage());
        return;
      }
      try {
        const items = await venueDashboardApiService.listVenueImages(venueId);
        const rawItems = Array.isArray(items) ? items : [];
        setRawVenueGalleryItems(rawItems);
        const hostPhotoUrl = resolveHostPhotoUrl({
          host_photo_url: hostPhotoUrlKey,
          id: venueId,
          images: rawItems
        });
        const normalized = rawItems
          .map((item, index) => normalizeVenueImage(item, index))
          .filter((item) => !isHostPhotoGalleryImage(item, hostPhotoUrl))
          .sort((a, b) => {
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return a.sortOrder - b.sortOrder;
          });
        setVenueImages(normalized);
      } catch (error) {
        setGalleryError(toGalleryErrorMessage(error));
      }
    };
    loadVenueImages();
  }, [venueId, hostPhotoUrlKey]);

  const normalizedAddOnItems = useMemo(() => {
    const mapped = addOnItems.map(normalizeAddOnItem).filter((item) => item.id || item.name);
    if (multiSpace && catalogSpaceId) {
      return filterRowsBySpace(mapped, catalogSpaceId, catalogSpaceOpts);
    }
    return mapped;
  }, [addOnItems, multiSpace, catalogSpaceId, catalogSpaceOpts]);

  const normalizedOtherItems = useMemo(() => {
    const mapped = otherItems.map(normalizeOtherItem).filter((item) => item.id || item.name);
    if (multiSpace && catalogSpaceId) {
      return filterRowsBySpace(mapped, catalogSpaceId, catalogSpaceOpts);
    }
    return mapped;
  }, [otherItems, multiSpace, catalogSpaceId, catalogSpaceOpts]);

  const normalizedPackages = useMemo(() => {
    const mapped = packages.map(normalizePackage).filter((pkg) => pkg.id || pkg.name);
    if (multiSpace && catalogSpaceId) {
      return filterPackagesBySpace(mapped, catalogSpaceId, catalogSpaceOpts);
    }
    return mapped;
  }, [packages, multiSpace, catalogSpaceId, catalogSpaceOpts]);


  const selectedPackageItems = useMemo(
    () => Object.values(packageForm.items || {}),
    [packageForm.items]
  );

  const calculatedPackagePrice = selectedPackageItems.reduce(
    (sum, item) => sum + getItemPrice(item) * getItemQuantity(item),
    0
  );

  const resolvedPackagePrice = packageForm.priceManuallyEdited
    ? Number.parseFloat(packageForm.customPrice) || 0
    : calculatedPackagePrice;

  useEffect(() => {
    if (!showPackageModal || packageForm.priceManuallyEdited) return;
    const nextPrice =
      calculatedPackagePrice > 0 ? Number(calculatedPackagePrice.toFixed(2)) : 0;
    setPackageForm((prev) => {
      const nextValue = nextPrice > 0 ? String(nextPrice) : '';
      if (prev.customPrice === nextValue) return prev;
      return { ...prev, customPrice: nextValue };
    });
  }, [calculatedPackagePrice, packageForm.priceManuallyEdited, showPackageModal]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tags = await venueDashboardApiService.getCatalogAddonTags();
        if (!cancelled && tags.length > 0) setCatalogTags(tags);
      } catch {
        if (!cancelled) setCatalogTags(DEFAULT_CATALOG_ADDON_TAGS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    catalogSyncedRef.current = null;
  }, [venueId]);

  useEffect(() => {
    if (!venueId) return;
    if (activeTab !== 'add-ons' && activeTab !== 'others' && activeTab !== 'packages') return;

    const syncKey = `${venueId}:${activeTab}:${catalogSpaceId || 'venue'}`;
    if (catalogSyncedRef.current === syncKey) return;

    let cancelled = false;
    (async () => {
      try {
        if (activeTab === 'packages') {
          const packagesData = await venueDashboardApiService.getVenuePackages(
            venueId,
            catalogSpaceId
          );
          if (cancelled || !Array.isArray(packagesData)) return;
          if (packagesData.length === 0 && packages.length > 0) {
            catalogSyncedRef.current = syncKey;
            return;
          }
          setPackages(packagesData.map((pkg) => normalizePackage(pkg)));
          catalogSyncedRef.current = syncKey;
          return;
        }

        if (activeTab === 'add-ons') {
          const addons = await venueDashboardApiService.getVenueAddOnItems(venueId, {
            itemKind: 'addon',
            spaceId: catalogSpaceId
          });
          if (cancelled || !Array.isArray(addons)) return;
          if (addons.length === 0 && addOnItems.length > 0) return;
          const normalizedAddons = addons.map((item) => normalizeAddOnItem(item));
          setAddOnItems(normalizedAddons);
          setAddOnItemsSnapshot(JSON.parse(JSON.stringify(normalizedAddons)));
          catalogSyncedRef.current = syncKey;
          return;
        }

        const others = await venueDashboardApiService.getVenueAddOnItems(venueId, {
          itemKind: 'other',
          spaceId: catalogSpaceId
        });
        if (cancelled || !Array.isArray(others)) return;
        if (others.length === 0 && otherItems.length > 0) return;
        const normalizedOthers = others.map((item) => normalizeOtherItem(item));
        setOtherItems(normalizedOthers);
        setOtherItemsSnapshot(JSON.parse(JSON.stringify(normalizedOthers)));
        catalogSyncedRef.current = syncKey;
      } catch {
        // Keep existing state if refresh fails, and retry next time this tab is opened.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    venueId,
    catalogSpaceId,
    setAddOnItems,
    setOtherItems,
    setPackages,
    addOnItems.length,
    otherItems.length,
    packages.length
  ]);

  const resetPackageForm = () => {
    setPackageForm({
      name: '',
      customPrice: '',
      priceManuallyEdited: false,
      availableFor: {
        tableBooking: true,
        fullVenueHire: false
      },
      items: {},
      tags: [],
    });
    setShowItemsDropdown(false);
    setEditingPackageId(null);
  };

  const closePackageModal = () => {
    setShowPackageModal(false);
    resetPackageForm();
  };

  const openPackageModalForCreate = () => {
    resetPackageForm();
    setShowPackageModal(true);
  };

  const openPackageModalForEdit = (pkg) => {
    const itemsMap = {};
    (pkg.items || []).forEach((item) => {
      const itemId = item.itemId || item.item_id || item.id;
      if (!itemId) return;
      const catalogItem = normalizedAddOnItems.find(
        (candidate) => String(candidate.id) === String(itemId)
      );
      itemsMap[itemId] = {
        itemId,
        id: itemId,
        name: catalogItem?.name || item.name || item.item_name || getItemLabel(item),
        price: catalogItem?.price ?? getItemPrice(item),
        quantity: getItemQuantity(item)
      };
    });
    const storedPrice = getPackageTotalPrice(pkg);
    const calculatedFromItems = Object.values(itemsMap).reduce(
      (sum, item) => sum + getItemPrice(item) * getItemQuantity(item),
      0
    );
    const priceDiffers =
      storedPrice > 0 &&
      calculatedFromItems > 0 &&
      Math.abs(storedPrice - calculatedFromItems) > 0.02;
    setPackageForm({
      name: pkg.name || '',
      customPrice:
        storedPrice > 0
          ? String(Number(storedPrice.toFixed(2)))
          : calculatedFromItems > 0
            ? String(Number(calculatedFromItems.toFixed(2)))
            : '',
      priceManuallyEdited: priceDiffers,
      availableFor: {
        tableBooking: pkg.availableFor?.tableBooking ?? pkg.availableFor?.table_booking ?? true,
        fullVenueHire: pkg.availableFor?.fullVenueHire ?? pkg.availableFor?.full_venue_hire ?? false
      },
      items: itemsMap,
      tags: normalizeTagSlugs(pkg.tags),
    });
    setEditingPackageId(pkg.id || pkg.package_id);
    setShowItemsDropdown(false);
    setShowPackageModal(true);
  };

  useEffect(() => {
    if (activeTab === 'others' && prevActiveTabRef.current !== 'others') {
      setOtherItemsSnapshot(JSON.parse(JSON.stringify(otherItems)));
    }
    if (activeTab === 'add-ons' && prevActiveTabRef.current !== 'add-ons') {
      setAddOnItemsSnapshot(JSON.parse(JSON.stringify(addOnItems)));
    }
    prevActiveTabRef.current = activeTab;
  }, [activeTab, addOnItems, otherItems]);

  useEffect(() => {
    if (activeTab === 'add-ons' && addOnItemsSnapshot.length === 0 && addOnItems.length > 0) {
      setAddOnItemsSnapshot(JSON.parse(JSON.stringify(addOnItems)));
    }
  }, [activeTab, addOnItems, addOnItemsSnapshot.length]);

  useEffect(() => {
    if (activeTab === 'others' && otherItemsSnapshot.length === 0 && otherItems.length > 0) {
      setOtherItemsSnapshot(JSON.parse(JSON.stringify(otherItems)));
    }
  }, [activeTab, otherItems, otherItemsSnapshot.length]);

  const getAddOnItemField = (item, field) => {
    if (field === 'name') {
      return item.name || item.item_name || item.title || '';
    }
    return item.price ?? item.price_per_unit ?? item.unit_price ?? '';
  };

  const hasAddOnItemEdits = useMemo(() => {
    if (addOnItemsSnapshot.length === 0) return false;
    const snapshotById = new Map(
      addOnItemsSnapshot.map((item) => [String(item.id || item.item_id), item])
    );
    return addOnItems.some((item) => {
      const id = String(item.id || item.item_id);
      const snapshot = snapshotById.get(id);
      if (!snapshot) return false;
      const nameChanged = getAddOnItemField(snapshot, 'name').trim() !== getAddOnItemField(item, 'name').trim();
      const priceChanged = Number(getAddOnItemField(snapshot, 'price')) !== Number(getAddOnItemField(item, 'price'));
      const tagsChanged = !tagsEqual(snapshot.tags, item.tags);
      return nameChanged || priceChanged || tagsChanged;
    });
  }, [addOnItems, addOnItemsSnapshot]);

  const updateExistingAddOnItem = (itemId, field, value) => {
    setAddOnItems((prev) => prev.map((item) => {
      const id = item.id || item.item_id;
      if (String(id) !== String(itemId)) return item;
      if (field === 'name') {
        return { ...item, name: value, item_name: value };
      }
      if (field === 'tags') {
        return { ...item, tags: normalizeTagSlugs(value) };
      }
      return { ...item, price: value, price_per_unit: value, unit_price: value };
    }));
  };

  const refreshVenueImages = async () => {
    if (!venueId) return;
    if (!hasBackendToken()) {
      setVenueImages([]);
      setGalleryError(getGalleryAuthMessage());
      return;
    }
    const items = await venueDashboardApiService.listVenueImages(venueId);
    const rawItems = Array.isArray(items) ? items : [];
    setRawVenueGalleryItems(rawItems);
    const hostPhotoUrl = resolveHostPhotoUrl({
      ...venueDetails,
      id: venueId,
      images: rawItems
    });
    const normalized = rawItems
      .map((item, index) => normalizeVenueImage(item, index))
      .filter((item) => !isHostPhotoGalleryImage(item, hostPhotoUrl))
      .sort((a, b) => {
        if (a.isPrimary && !b.isPrimary) return -1;
        if (!a.isPrimary && b.isPrimary) return 1;
        return a.sortOrder - b.sortOrder;
      });
    setVenueImages(normalized);
  };

  const handleImageFilesSelected = (event) => {
    const files = Array.from(event.target.files || []);
    setGalleryError('');
    setSelectedImageFiles(files);
  };

  const handleUploadVenueImages = async () => {
    if (!venueId || selectedImageFiles.length === 0 || galleryBusy) return;
    if (!hasBackendToken()) {
      setGalleryError(getGalleryAuthMessage());
      return;
    }
    const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
    const invalid = selectedImageFiles.find((file) => !allowedTypes.has(file.type));
    if (invalid) {
      setGalleryError(`Unsupported file type: ${invalid.name}. Use JPG, PNG, or WebP.`);
      return;
    }

    try {
      setGalleryBusy(true);
      setGalleryError('');
      for (let index = 0; index < selectedImageFiles.length; index += 1) {
        const file = selectedImageFiles[index];
        const uploadInfo = await venueDashboardApiService.createVenueImageUploadUrl(venueId, {
          filename: file.name,
          contentType: file.type,
          replaceAll: replaceAllImages && index === 0
        });
        await venueDashboardApiService.uploadVenueImageFile(
          uploadInfo.upload_url,
          file,
          uploadInfo.suggested_headers || uploadInfo.required_headers || {}
        );
        await venueDashboardApiService.completeVenueImageUpload(venueId, uploadInfo.image_id || uploadInfo.id);
      }
      await refreshVenueImages();
      setSelectedImageFiles([]);
      setReplaceAllImages(false);
      if (onNotify) {
        onNotify({
          type: 'success',
          title: 'Venue photos uploaded',
          message: `${selectedImageFiles.length} image(s) uploaded successfully.`
        });
      }
    } catch (error) {
      setGalleryError(toGalleryErrorMessage(error));
    } finally {
      setGalleryBusy(false);
    }
  };

  const handleSetPrimaryImage = async (imageId) => {
    if (!venueId || !imageId || galleryBusy) return;
    if (!hasBackendToken()) {
      setGalleryError(getGalleryAuthMessage());
      return;
    }
    try {
      setGalleryBusy(true);
      setGalleryError('');
      await venueDashboardApiService.setPrimaryVenueImage(venueId, imageId);
      await refreshVenueImages();
    } catch (error) {
      setGalleryError(toGalleryErrorMessage(error));
    } finally {
      setGalleryBusy(false);
    }
  };

  const handleCaptionChange = (imageId, value) => {
    setVenueImages((previous) =>
      previous.map((image) =>
        image.id === imageId ? { ...image, caption: value } : image
      )
    );
  };

  const handleSaveCaption = async (imageId, caption) => {
    if (!venueId || !imageId || galleryBusy) return;
    if (!hasBackendToken()) {
      setGalleryError(getGalleryAuthMessage());
      return;
    }
    try {
      setGalleryBusy(true);
      setGalleryError('');
      await venueDashboardApiService.patchVenueImage(venueId, imageId, { caption });
      await refreshVenueImages();
    } catch (error) {
      setGalleryError(toGalleryErrorMessage(error));
    } finally {
      setGalleryBusy(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!venueId || !imageId || galleryBusy) return;
    if (!hasBackendToken()) {
      setGalleryError(getGalleryAuthMessage());
      return;
    }
    try {
      setGalleryBusy(true);
      setGalleryError('');
      await venueDashboardApiService.deleteVenueImage(venueId, imageId);
      await refreshVenueImages();
    } catch (error) {
      setGalleryError(toGalleryErrorMessage(error));
    } finally {
      setGalleryBusy(false);
    }
  };

  const handleDeleteAllImages = async () => {
    if (!venueId || galleryBusy) return;
    if (!hasBackendToken()) {
      setGalleryError(getGalleryAuthMessage());
      return;
    }
    const shouldDelete = window.confirm('Delete all venue photos? This action cannot be undone.');
    if (!shouldDelete) return;
    try {
      setGalleryBusy(true);
      setGalleryError('');
      await venueDashboardApiService.deleteAllVenueImages(venueId);
      await refreshVenueImages();
    } catch (error) {
      setGalleryError(toGalleryErrorMessage(error));
    } finally {
      setGalleryBusy(false);
    }
  };

  const handleAddOnCreate = async (itemsToCreate) => {
    if (!venueId) return;
    if (multiSpace && !catalogSpaceId) {
      setItemsError('Select a space before adding items.');
      return;
    }
    setItemsError('');
    const sanitizedItems = itemsToCreate
      .map(item => ({
        name: item.name.trim(),
        price: parseFloat(item.price),
        tags: normalizeTagSlugs(item.tags),
      }))
      .filter(item => item.name && !Number.isNaN(item.price));

    if (sanitizedItems.length === 0) {
      setItemsError('Enter an item name and price to save.');
      return;
    }

    try {
      setIsSavingItem(true);
      const createdItems = [];
      for (const item of sanitizedItems) {
      const payload = {
        name: item.name,
        price: item.price,
        is_active: true,
        item_kind: ADDON_ITEM_KIND.ADDON,
        pricing_unit: ADDON_PRICING_UNITS.PER_PERSON,
        tags: item.tags,
        ...(catalogSpaceId ? { space_id: catalogSpaceId } : {})
      };
        const created = await venueDashboardApiService.createVenueAddOnItem(venueId, payload);
        createdItems.push(normalizeAddOnItem(created, {
          name: item.name,
          price: item.price,
          tags: item.tags,
        }));
      }
      setAddOnItems(prev => {
        const next = [...prev, ...createdItems];
        setAddOnItemsSnapshot(JSON.parse(JSON.stringify(next)));
        return next;
      });
      setDraftAddOnItems([]);
    } catch (error) {
      setItemsError('Unable to save item. Please try again.');
    } finally {
      setIsSavingItem(false);
    }
  };

  const handleAddOnItemUpdates = async () => {
    if (!venueId || !hasAddOnItemEdits) return;
    const snapshotById = new Map(
      addOnItemsSnapshot.map((item) => [String(item.id || item.item_id), item])
    );
    const itemsToUpdate = addOnItems.filter((item) => {
      const id = String(item.id || item.item_id);
      const snapshot = snapshotById.get(id);
      if (!snapshot) return false;
      const nameChanged = getAddOnItemField(snapshot, 'name').trim() !== getAddOnItemField(item, 'name').trim();
      const priceChanged = Number(getAddOnItemField(snapshot, 'price')) !== Number(getAddOnItemField(item, 'price'));
      const tagsChanged = !tagsEqual(snapshot.tags, item.tags);
      return nameChanged || priceChanged || tagsChanged;
    });

    if (itemsToUpdate.length === 0) return;

    setIsSavingItem(true);
    try {
      const updatedItems = new Map();
      for (const item of itemsToUpdate) {
        const itemId = item.id || item.item_id;
        const name = getAddOnItemField(item, 'name').trim();
        const price = Number(getAddOnItemField(item, 'price'));
        if (!name || Number.isNaN(price)) {
          setItemsError('Each item needs a name and valid price.');
          throw new Error('Invalid add-on item');
        }
        const updated = await venueDashboardApiService.updateVenueAddOnItem(venueId, itemId, {
          name,
          price,
          tags: normalizeTagSlugs(item.tags),
        });
        updatedItems.set(String(itemId), normalizeAddOnItem(updated, { name, price, tags: item.tags }));
      }
      setAddOnItems((prev) => {
        const next = prev.map((existing) => {
          const id = String(existing.id || existing.item_id);
          return updatedItems.has(id) ? updatedItems.get(id) : existing;
        });
        setAddOnItemsSnapshot(JSON.parse(JSON.stringify(next)));
        return next;
      });
    } catch (error) {
      setItemsError('Unable to update item(s). Please try again.');
      throw error;
    } finally {
      setIsSavingItem(false);
    }
  };

  const handleAddOnDelete = (itemId, itemIndex) => {
    setItemsError('');
    if (itemId) {
      setPendingItemDeletes(prev => [...prev, itemId]);
      setAddOnItems(prev => prev.filter(item => (item.id || item.item_id) !== itemId));
    } else if (typeof itemIndex === 'number') {
      setAddOnItems(prev => prev.filter((_, index) => index !== itemIndex));
    }
  };

  const applyPendingItemDeletes = async () => {
    if (pendingItemDeletes.length === 0) return;
    const deletedIds = [...pendingItemDeletes];
    try {
      for (const id of deletedIds) {
        await venueDashboardApiService.deleteVenueAddOnItem(venueId, id);
      }
      setPendingItemDeletes([]);
      setAddOnItemsSnapshot((prev) => prev.filter(
        (item) => !deletedIds.includes(item.id || item.item_id)
      ));
    } catch (error) {
      setItemsError('Unable to delete item(s). Please try again.');
      throw error;
    }
  };

  const getOtherItemField = (item, field) => {
    if (field === 'name') {
      return item.name || item.item_name || item.title || '';
    }
    if (field === 'pricing_unit') {
      return getAddonPricingUnit(item);
    }
    if (field === 'default_quantity') {
      return item.default_quantity ?? item.defaultQuantity ?? 1;
    }
    return item.price ?? item.price_per_unit ?? item.unit_price ?? '';
  };

  const hasOtherItemEdits = useMemo(() => {
    if (otherItemsSnapshot.length === 0) return false;
    const snapshotById = new Map(
      otherItemsSnapshot.map((item) => [String(item.id || item.item_id), item])
    );
    return otherItems.some((item) => {
      const id = String(item.id || item.item_id);
      const snapshot = snapshotById.get(id);
      if (!snapshot) return false;
      const nameChanged = getOtherItemField(snapshot, 'name').trim() !== getOtherItemField(item, 'name').trim();
      const priceChanged = Number(getOtherItemField(snapshot, 'price')) !== Number(getOtherItemField(item, 'price'));
      const unitChanged = getOtherItemField(snapshot, 'pricing_unit') !== getOtherItemField(item, 'pricing_unit');
      const qtyChanged = Number(getOtherItemField(snapshot, 'default_quantity')) !== Number(getOtherItemField(item, 'default_quantity'));
      const tagsChanged = !tagsEqual(snapshot.tags, item.tags);
      return nameChanged || priceChanged || unitChanged || qtyChanged || tagsChanged;
    });
  }, [otherItems, otherItemsSnapshot]);

  const updateExistingOtherItem = (itemId, field, value) => {
    setOtherItems((prev) => prev.map((item) => {
      const id = item.id || item.item_id;
      if (String(id) !== String(itemId)) return item;
      if (field === 'name') {
        return { ...item, name: value, item_name: value };
      }
      if (field === 'tags') {
        return { ...item, tags: normalizeTagSlugs(value) };
      }
      if (field === 'pricing_unit') {
        const pricingUnit = value;
        const defaultQty = pricingUnit === ADDON_PRICING_UNITS.PER_EVENT ? 1 : (item.default_quantity ?? 1);
        return { ...item, pricing_unit: pricingUnit, default_quantity: defaultQty };
      }
      if (field === 'default_quantity') {
        return { ...item, default_quantity: value };
      }
      return { ...item, price: value, price_per_unit: value, unit_price: value };
    }));
  };

  const handleOtherCreate = async (itemsToCreate) => {
    if (!venueId) return;
    setItemsError('');
    const sanitizedItems = itemsToCreate
      .map((item) => {
        const pricingUnit = item.pricing_unit || ADDON_PRICING_UNITS.PER_NUMBER;
        const defaultQty = pricingUnit === ADDON_PRICING_UNITS.PER_EVENT
          ? 1
          : parseFloat(item.default_quantity);
        return {
          name: item.name.trim(),
          price: parseFloat(item.price),
          pricing_unit: pricingUnit,
          default_quantity: Number.isNaN(defaultQty) ? 1 : defaultQty,
          tags: normalizeTagSlugs(item.tags),
        };
      })
      .filter((item) => item.name && !Number.isNaN(item.price));

    if (sanitizedItems.length === 0) {
      setItemsError('Enter an item name and price to save.');
      return;
    }

    try {
      setIsSavingItem(true);
      const createdItems = [];
      for (const item of sanitizedItems) {
        const payload = {
          name: item.name,
          price: item.price,
          is_active: true,
          item_kind: ADDON_ITEM_KIND.OTHER,
          pricing_unit: item.pricing_unit,
          default_quantity: item.default_quantity,
          tags: item.tags,
          ...(catalogSpaceId ? { space_id: catalogSpaceId } : {})
        };
        const created = await venueDashboardApiService.createVenueAddOnItem(venueId, payload);
        createdItems.push(normalizeOtherItem(created, item));
      }
      setOtherItems((prev) => {
        const next = [...prev, ...createdItems];
        setOtherItemsSnapshot(JSON.parse(JSON.stringify(next)));
        return next;
      });
      setDraftOtherItems([]);
    } catch (error) {
      setItemsError('Unable to save item. Please try again.');
    } finally {
      setIsSavingItem(false);
    }
  };

  const handleOtherItemUpdates = async () => {
    if (!venueId || !hasOtherItemEdits) return;
    const snapshotById = new Map(
      otherItemsSnapshot.map((item) => [String(item.id || item.item_id), item])
    );
    const itemsToUpdate = otherItems.filter((item) => {
      const id = String(item.id || item.item_id);
      const snapshot = snapshotById.get(id);
      if (!snapshot) return false;
      const nameChanged = getOtherItemField(snapshot, 'name').trim() !== getOtherItemField(item, 'name').trim();
      const priceChanged = Number(getOtherItemField(snapshot, 'price')) !== Number(getOtherItemField(item, 'price'));
      const unitChanged = getOtherItemField(snapshot, 'pricing_unit') !== getOtherItemField(item, 'pricing_unit');
      const qtyChanged = Number(getOtherItemField(snapshot, 'default_quantity')) !== Number(getOtherItemField(item, 'default_quantity'));
      const tagsChanged = !tagsEqual(snapshot.tags, item.tags);
      return nameChanged || priceChanged || unitChanged || qtyChanged || tagsChanged;
    });

    if (itemsToUpdate.length === 0) return;

    setIsSavingItem(true);
    try {
      const updatedItems = new Map();
      for (const item of itemsToUpdate) {
        const itemId = item.id || item.item_id;
        const name = getOtherItemField(item, 'name').trim();
        const price = Number(getOtherItemField(item, 'price'));
        const pricingUnit = getOtherItemField(item, 'pricing_unit');
        const defaultQuantity = pricingUnit === ADDON_PRICING_UNITS.PER_EVENT
          ? 1
          : Number(getOtherItemField(item, 'default_quantity'));
        if (!name || Number.isNaN(price)) {
          setItemsError('Each item needs a name and valid price.');
          throw new Error('Invalid other item');
        }
        const updated = await venueDashboardApiService.updateVenueAddOnItem(venueId, itemId, {
          name,
          price,
          item_kind: ADDON_ITEM_KIND.OTHER,
          pricing_unit: pricingUnit,
          default_quantity: Number.isNaN(defaultQuantity) ? 1 : defaultQuantity,
          tags: normalizeTagSlugs(item.tags),
        });
        updatedItems.set(String(itemId), normalizeOtherItem(updated, {
          name,
          price,
          pricing_unit: pricingUnit,
          default_quantity: defaultQuantity,
          tags: item.tags,
        }));
      }
      setOtherItems((prev) => {
        const next = prev.map((existing) => {
          const id = String(existing.id || existing.item_id);
          return updatedItems.has(id) ? updatedItems.get(id) : existing;
        });
        setOtherItemsSnapshot(JSON.parse(JSON.stringify(next)));
        return next;
      });
    } catch (error) {
      setItemsError('Unable to update item(s). Please try again.');
      throw error;
    } finally {
      setIsSavingItem(false);
    }
  };

  const handleOtherDelete = (itemId, itemIndex) => {
    setItemsError('');
    if (itemId) {
      setPendingOtherDeletes((prev) => [...prev, itemId]);
      setOtherItems((prev) => prev.filter((item) => (item.id || item.item_id) !== itemId));
    } else if (typeof itemIndex === 'number') {
      setOtherItems((prev) => prev.filter((_, index) => index !== itemIndex));
    }
  };

  const applyPendingOtherDeletes = async () => {
    if (pendingOtherDeletes.length === 0) return;
    const deletedIds = [...pendingOtherDeletes];
    try {
      for (const id of deletedIds) {
        await venueDashboardApiService.deleteVenueAddOnItem(venueId, id);
      }
      setPendingOtherDeletes([]);
      setOtherItemsSnapshot((prev) => prev.filter(
        (item) => !deletedIds.includes(item.id || item.item_id)
      ));
    } catch (error) {
      setItemsError('Unable to delete item(s). Please try again.');
      throw error;
    }
  };

  const toApiItemsFormat = (items) => {
    if (!Array.isArray(items) || items.length === 0) return [];
    return items
      .map((item) => {
        const itemId =
          item.item_id ??
          item.itemId ??
          item.add_on_item_id ??
          item.id ??
          item.item?.id ??
          item.item?.item_id ??
          item.item?.itemId;
        return {
          item_id: itemId,
          quantity: getItemQuantity(item)
        };
      })
      .filter((i) => i.item_id != null && i.item_id !== '');
  };

  const handlePackageToggle = async (pkg) => {
    if (!venueId || !pkg.id) return;
    setPackagesError('');
    try {
      const items = toApiItemsFormat(pkg.items);
      const price = getPackageTotalPrice(pkg) || pkg.price;
      const payload = {
        name: pkg.name,
        price: Number(price) || 0,
        is_active: !pkg.isActive
      };
      if (items.length > 0) {
        payload.items = items;
      }
      const updated = await venueDashboardApiService.updateVenuePackage(venueId, pkg.id, payload);
      const normalized = normalizePackage(updated);
      const hasItemsFromApi =
        normalized.items && Array.isArray(normalized.items) && normalized.items.length > 0;
      const merged = {
        ...normalized,
        items: hasItemsFromApi ? normalized.items : (pkg.items || [])
      };
      if (!hasItemsFromApi && (pkg.items || []).length > 0) {
        try {
          const refreshed = await venueDashboardApiService.getVenuePackages(venueId, catalogSpaceId);
          setPackages(refreshed.map(normalizePackage));
          return;
        } catch (_) {
          /* fall through to merged */
        }
      }
      setPackages((prev) =>
        prev.map((item) => ((item.id || item.package_id) === pkg.id ? merged : item))
      );
    } catch (error) {
      setPackagesError('Unable to update package status.');
    }
  };

  const handlePackageDelete = async (pkgId) => {
    if (!venueId || !pkgId) return;
    setPackagesError('');
    const previousPackages = packages;
    setPackages(prev => prev.filter(item => (item.id || item.package_id) !== pkgId));
    try {
      await venueDashboardApiService.deleteVenuePackage(venueId, pkgId);
    } catch (error) {
      setPackages(previousPackages);
      setPackagesError('Unable to delete package. Please try again.');
    }
  };

  const togglePackageItem = (item) => {
    setPackageForm(prev => {
      const nextItems = { ...(prev.items || {}) };
      const itemId = item.id || item.item_id;
      if (nextItems[itemId]) {
        delete nextItems[itemId];
      } else {
        nextItems[itemId] = {
          ...item,
          itemId,
          quantity: 1
        };
      }
      return { ...prev, items: nextItems };
    });
  };

  const updatePackageItemQuantity = (itemId, quantity) => {
    setPackageForm(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemId]: {
          ...prev.items[itemId],
          quantity
        }
      }
    }));
  };

  const notifyPackageSaved = (packageName, isEdit) => {
    const payload = {
      type: 'success',
      title: isEdit ? 'Package updated!' : 'Package created!',
      message: isEdit
        ? `"${packageName}" has been updated successfully.`
        : `"${packageName}" has been created successfully.`
    };
    if (onNotify) {
      onNotify(payload);
    } else {
      window.alert(payload.message);
    }
  };

  const handleSavePackage = async () => {
    if (!venueId) return;
    setPackagesError('');
    if (!packageForm.name.trim() || selectedPackageItems.length === 0) {
      setPackagesError('Add a package name and at least one item.');
      return;
    }
    try {
      setIsSavingPackage(true);
      const payloadItems = selectedPackageItems
        .map(item => ({
          item_id: item.itemId || item.id || item.item_id,
          quantity: getItemQuantity(item)
        }))
        .filter(item => item.item_id);

      if (payloadItems.length === 0) {
        setPackagesError('Selected items are missing IDs.');
        return;
      }

      const packageName = packageForm.name.trim();
      const packagePrice = Number(resolvedPackagePrice.toFixed(2));
      if (packagePrice <= 0) {
        setPackagesError('Enter a package price greater than zero.');
        return;
      }
      const payload = {
        name: packageName,
        price: packagePrice,
        items: payloadItems,
        tags: normalizeTagSlugs(packageForm.tags),
        ...(catalogSpaceId ? { space_ids: [catalogSpaceId] } : {})
      };

      if (editingPackageId) {
        const existingPackage = normalizedPackages.find(
          (pkg) => String(pkg.id) === String(editingPackageId)
        );
        payload.is_active = existingPackage?.isActive ?? true;
        await venueDashboardApiService.updateVenuePackage(
          venueId,
          editingPackageId,
          payload
        );
        try {
          const refreshed = await venueDashboardApiService.getVenuePackages(venueId, catalogSpaceId);
          if (Array.isArray(refreshed)) {
            setPackages(refreshed.map((pkg) => normalizePackage(pkg)));
          }
        } catch {
          setPackages((prev) =>
            prev.map((item) => {
              if (String(item.id || item.package_id) !== String(editingPackageId)) return item;
              return {
                ...item,
                name: packageName,
                price: packagePrice,
                tags: normalizeTagSlugs(packageForm.tags),
                items: selectedPackageItems.map((item) => ({
                  ...item,
                  item_id: item.itemId || item.id || item.item_id,
                  quantity: getItemQuantity(item)
                }))
              };
            })
          );
        }
        closePackageModal();
        notifyPackageSaved(packageName, true);
        return;
      }

      const created = await venueDashboardApiService.createVenuePackage(venueId, payload);
      try {
        const normalizedCreated = created && (created.id || created.package_id) ? normalizePackage(created) : null;
        if (normalizedCreated) {
          const hasItemsFromApi = normalizedCreated.items?.length > 0;
          const packageTags = normalizeTagSlugs(normalizedCreated.tags ?? packageForm.tags);
          const merged = hasItemsFromApi
            ? { ...normalizedCreated, price: packagePrice, tags: packageTags }
            : {
              ...normalizedCreated,
              price: packagePrice,
              tags: packageTags,
              items: selectedPackageItems.map(i => ({
                ...i,
                item_id: i.itemId || i.id || i.item_id,
                quantity: getItemQuantity(i)
              }))
            };
          const displayName = (merged.name && merged.name.trim() && merged.name !== 'Package')
            ? merged.name
            : packageName;
          setPackages(prev => [...prev, { ...merged, name: displayName }]);
        } else {
          const refreshed = await venueDashboardApiService.getVenuePackages(venueId, catalogSpaceId);
          setPackages(refreshed.map(normalizePackage));
        }
      } catch {
        try {
          const refreshed = await venueDashboardApiService.getVenuePackages(venueId, catalogSpaceId);
          if (Array.isArray(refreshed)) {
            setPackages(refreshed.map((pkg) => normalizePackage(pkg)));
          }
        } catch {
          // Package was created; list will refresh on next tab visit
        }
      }
      closePackageModal();
      notifyPackageSaved(packageName, false);
    } catch (error) {
      setPackagesError(
        editingPackageId
          ? 'Unable to update package. Please try again.'
          : 'Unable to create package. Please try again.'
      );
    } finally {
      setIsSavingPackage(false);
    }
  };

  const getPackageSummary = (pkg) => {
    if (!pkg.items || pkg.items.length === 0) {
      return 'No items added';
    }
    const itemLookup = normalizedAddOnItems.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
    return pkg.items
      .map(item => {
        const quantity = getItemQuantity(item);
        const name =
          item.name ||
          item.item_name ||
          item.item?.name ||
          item.item?.item_name ||
          itemLookup[item.item_id]?.name ||
          itemLookup[item.itemId]?.name ||
          itemLookup[item.item?.id]?.name ||
          'Item';
        return `${quantity} ${name}`;
      })
      .join(' + ');
  };

  const canSaveAddOn = useMemo(() => (
    draftAddOnItems.some(item => item.name.trim() && item.price)
    || pendingItemDeletes.length > 0
    || hasAddOnItemEdits
  ), [draftAddOnItems, pendingItemDeletes, hasAddOnItemEdits]);
  const canSaveOther = useMemo(() => (
    draftOtherItems.some((item) => item.name.trim() && item.price)
    || pendingOtherDeletes.length > 0
    || hasOtherItemEdits
  ), [draftOtherItems, pendingOtherDeletes, hasOtherItemEdits]);
  const canSavePackage =
    packageForm.name.trim() &&
    selectedPackageItems.length > 0 &&
    resolvedPackagePrice > 0;
  const isHeaderSaveDisabled =
    (activeTab === 'add-ons' && (!canSaveAddOn || isSavingItem))
    || (activeTab === 'others' && (!canSaveOther || isSavingItem))
    || (activeTab === 'packages' && (!showPackageModal || !canSavePackage || isSavingPackage));

  const handleHeaderSave = async () => {
    if (activeTab === 'general') {
      if (onSaveVenueDetails) {
        await onSaveVenueDetails();
      } else {
      }
    } else if (activeTab === 'add-ons') {
      try {
        if (pendingItemDeletes.length > 0) {
          await applyPendingItemDeletes();
        }
        if (hasAddOnItemEdits) {
          await handleAddOnItemUpdates();
        }
        if (draftAddOnItems.length > 0) {
          await handleAddOnCreate(draftAddOnItems);
        }
        if (pendingItemDeletes.length > 0 || draftAddOnItems.length > 0 || hasAddOnItemEdits) {
          setItemsError('');
          if (onNotify) {
            onNotify({
              type: 'success',
              title: 'Items saved!',
              message: 'Your items have been saved successfully.'
            });
          }
        }
      } catch (_) {
        // Error already set in applyPendingItemDeletes / handleAddOnItemUpdates
      }
    } else if (activeTab === 'others') {
      try {
        if (pendingOtherDeletes.length > 0) {
          await applyPendingOtherDeletes();
        }
        if (hasOtherItemEdits) {
          await handleOtherItemUpdates();
        }
        if (draftOtherItems.length > 0) {
          await handleOtherCreate(draftOtherItems);
        }
        if (pendingOtherDeletes.length > 0 || draftOtherItems.length > 0 || hasOtherItemEdits) {
          setItemsError('');
          if (onNotify) {
            onNotify({
              type: 'success',
              title: 'Others saved!',
              message: 'Your other items have been saved successfully.'
            });
          }
        }
      } catch (_) {
        // Error already set in applyPendingOtherDeletes / handleOtherItemUpdates
      }
    } else if (activeTab === 'packages' && showPackageModal) {
      await handleSavePackage();
    }
  };

  const handleHeaderCancel = () => {
    if (activeTab === 'add-ons') {
      setItemsError('');
      setDraftAddOnItems([]);
      setPendingItemDeletes([]);
      if (addOnItemsSnapshot.length > 0) {
        setAddOnItems(addOnItemsSnapshot);
      }
    }
    if (activeTab === 'others') {
      setItemsError('');
      setDraftOtherItems([]);
      setPendingOtherDeletes([]);
      if (otherItemsSnapshot.length > 0) {
        setOtherItems(otherItemsSnapshot);
      }
    }
    if (activeTab === 'packages') {
      resetPackageForm();
      setPackagesError('');
      setShowPackageModal(false);
    }
  };

  const removeDraftRow = (indexToRemove) => {
    setDraftAddOnItems(items => items.filter((_, index) => index !== indexToRemove));
  };

  const removeOtherDraftRow = (indexToRemove) => {
    setDraftOtherItems((items) => items.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="settings-panel venue-details-panel" style={{ width: '100%' }}>
      <div className="venue-details-header">
        <h3>Venue details</h3>
        <div className="venue-details-actions">
          <button className="btn-secondary" onClick={handleHeaderCancel}>Cancel</button>
          <button
            className="btn-primary"
            onClick={handleHeaderSave}
            disabled={isHeaderSaveDisabled}
          >
            Save
          </button>
        </div>
      </div>

      <div
        className="venue-details-tabs"
        style={{
          display: 'flex',
          gap: '32px',
          rowGap: '12px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <button
          className={`venue-details-tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General details
        </button>
        <button
          className={`venue-details-tab ${activeTab === 'add-ons' ? 'active' : ''}`}
          onClick={() => setActiveTab('add-ons')}
        >
          Items
        </button>
        <button
          className={`venue-details-tab ${activeTab === 'packages' ? 'active' : ''}`}
          onClick={() => setActiveTab('packages')}
        >
          Packages
        </button>
        <button
          className={`venue-details-tab ${activeTab === 'others' ? 'active' : ''}`}
          onClick={() => setActiveTab('others')}
        >
          Others
        </button>
      </div>

      {multiSpace && activeTab !== 'general' && onSettingsSpaceIdChange ? (
        <SettingsSpaceScopeBar
          spaces={venueSpaces}
          value={settingsSpaceId}
          onChange={onSettingsSpaceIdChange}
          venueWideLabel="All spaces"
          className="venue-details-space-scope"
        />
      ) : null}

      {activeTab === 'general' && (
        <div className="venue-details-general-section">
          <label className="venue-details-section-title">General details</label>
          {!venueDetails ? (
            <p className="venue-details-loading">Loading venue details...</p>
          ) : (
          <div className="venue-details-general-fields">
            <div className="venue-details-general-row">
              <div className="venue-details-general-field">
                <label className="venue-details-field-label">
                  <span className="label-text">Venue name</span>
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  className="venue-details-input"
                  value={venueDetails?.name || ''}
                  onChange={(e) => setVenueDetails?.({ ...venueDetails, name: e.target.value })}
                  placeholder="Enter venue name"
                  readOnly={!setVenueDetails}
                />
              </div>
              <div className="venue-details-general-field">
                <label className="venue-details-field-label">
                  <span className="label-text">Venue type</span>
                  <span className="required-asterisk">*</span>
                </label>
                <div className="venue-details-select-wrapper">
                  <select
                    className="venue-details-input venue-details-select"
                    value={venueDetails?.venue_type || venueDetails?.type || ''}
                    onChange={(e) => setVenueDetails?.({ ...venueDetails, venue_type: e.target.value })}
                    disabled={!setVenueDetails}
                  >
                    <option value="">Select venue type</option>
                    {VENUE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <FigmaChevronDownIcon className="venue-details-select-chevron" />
                </div>
              </div>
            </div>
            <div className="venue-details-general-row">
              <div className="venue-details-general-field venue-details-general-field--full">
                <label className="venue-details-field-label">
                  <span className="label-text">Address</span>
                  <span className="required-asterisk">*</span>
                  {!isAdmin && <span className="venue-details-admin-only">Admin only</span>}
                </label>
                <input
                  type="text"
                  className="venue-details-input"
                  value={venueDetails?.address || ''}
                  onChange={(e) => setVenueDetails?.({ ...venueDetails, address: e.target.value })}
                  placeholder="Enter full address"
                  readOnly={!setVenueDetails || !isAdmin}
                />
              </div>
            </div>
            <div className="venue-details-general-row">
              <div className="venue-details-general-field venue-details-general-field--full">
                <label className="venue-details-field-label">
                  <span className="label-text">Map link</span>
                  <span className="required-asterisk">*</span>
                  {!isAdmin && <span className="venue-details-admin-only">Admin only</span>}
                </label>
                <input
                  type="url"
                  className="venue-details-input"
                  value={venueDetails?.map_link || venueDetails?.mapLink || ''}
                  onChange={(e) => setVenueDetails?.({ ...venueDetails, map_link: e.target.value })}
                  placeholder="https://maps.app.goo.gl/..."
                  readOnly={!setVenueDetails || !isAdmin}
                />
              </div>
            </div>
            <div className="venue-details-general-row">
              <div className="venue-details-general-field venue-details-general-field--full">
                <label className="venue-details-field-label">
                  <span className="label-text">Host photo</span>
                </label>
                <p className="venue-details-field-help">
                  Shown on the booking page and venue details (&ldquo;Hosted by&rdquo;). Upload a square
                  photo for each venue; re-upload to replace.
                </p>
                <div className="venue-host-photo-upload">
                  <div className="venue-host-photo-preview">
                    {hostPhotoDisplayUrl && !hostPhotoImageError ? (
                      <img
                        src={hostPhotoDisplayUrl}
                        alt=""
                        onError={() => setHostPhotoImageError(true)}
                      />
                    ) : (
                      <div className="venue-host-photo-placeholder" aria-hidden="true">
                        <span className="venue-host-photo-placeholder-initials">
                          {hostPhotoInitials}
                        </span>
                        <span className="venue-host-photo-placeholder-label">
                          {hasSavedHostPhoto || selectedHostPhotoFile
                            ? 'Preview unavailable'
                            : 'No photo yet'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="venue-host-photo-controls">
                    <label className="venue-host-photo-file-label">
                      <span className="btn-secondary venue-host-photo-file-button">
                        Choose photo
                      </span>
                      <input
                        type="file"
                        className="venue-host-photo-file-input"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleHostPhotoFileSelected}
                        disabled={hostPhotoBusy || !canManageHostPhoto}
                      />
                    </label>
                    {selectedHostPhotoFile ? (
                      <p className="venue-gallery-selected-files">
                        Selected: {selectedHostPhotoFile.name}
                        {hostPhotoLocalPreviewUrl ? ' — preview updated' : ''}
                      </p>
                    ) : (
                      <p className="venue-details-field-hint venue-host-photo-file-hint">
                        Square JPG, PNG, or WebP (min. 256×256).
                      </p>
                    )}
                    <div className="venue-gallery-upload-actions">
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleUploadHostPhoto}
                        disabled={
                          hostPhotoBusy || !canManageHostPhoto || !selectedHostPhotoFile
                        }
                      >
                        {hostPhotoBusy ? 'Uploading...' : 'Upload host photo'}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleRemoveHostPhoto}
                        disabled={hostPhotoBusy || !canManageHostPhoto || !hasSavedHostPhoto}
                      >
                        Remove photo
                      </button>
                    </div>
                    {hostPhotoError ? <p className="inline-error">{hostPhotoError}</p> : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="venue-details-general-row">
              <div className="venue-details-general-field venue-details-general-field--full">
                <label className="venue-details-field-label">
                  <span className="label-text">Venue photos (gallery)</span>
                </label>
                <div className="venue-gallery-upload-box">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleImageFilesSelected}
                    disabled={galleryBusy || !canManageGallery}
                  />
                  <p className="venue-details-field-hint">
                    Recommended size: 2000x1600 (5:4). Use JPG, PNG, or WebP.
                  </p>
                  {selectedImageFiles.length > 0 ? (
                    <p className="venue-gallery-selected-files">
                      {selectedImageFiles.length} file(s) selected
                    </p>
                  ) : null}
                  <label className="venue-gallery-replace-all">
                    <input
                      type="checkbox"
                      checked={replaceAllImages}
                      onChange={(event) => setReplaceAllImages(event.target.checked)}
                      disabled={galleryBusy || !canManageGallery}
                    />
                    <span>Replace all existing photos with this upload batch</span>
                  </label>
                  <div className="venue-gallery-upload-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleUploadVenueImages}
                      disabled={galleryBusy || !canManageGallery || selectedImageFiles.length === 0}
                    >
                      {galleryBusy ? 'Uploading...' : 'Upload photos'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleDeleteAllImages}
                      disabled={galleryBusy || !canManageGallery || venueImages.length === 0}
                    >
                      Delete all photos
                    </button>
                  </div>
                  {galleryError ? (
                    <p className="inline-error">{galleryError}</p>
                  ) : null}
                  {venueImages.length > 0 ? (
                    <div className="venue-gallery-images-list">
                      {venueImages.map((image) => (
                        <div className="venue-gallery-image-card" key={image.id}>
                          <div className="venue-gallery-image-preview">
                            {image.imageUrl ? (
                              <img src={image.imageUrl} alt={image.caption || 'Venue'} />
                            ) : (
                              <span>{image.uploadStatus || 'pending'}</span>
                            )}
                          </div>
                          <div className="venue-gallery-image-meta">
                            <div className="venue-gallery-image-badges">
                              {image.isPrimary ? (
                                <span className="venue-gallery-badge venue-gallery-badge--primary">
                                  Primary
                                </span>
                              ) : null}
                              {image.uploadStatus ? (
                                <span className="venue-gallery-badge">{image.uploadStatus}</span>
                              ) : null}
                            </div>
                            <textarea
                              className="venue-details-input venue-details-textarea"
                              rows={2}
                              value={image.caption}
                              onChange={(event) => handleCaptionChange(image.id, event.target.value)}
                              placeholder="Optional caption"
                              disabled={galleryBusy || !canManageGallery}
                            />
                            <div className="venue-gallery-image-actions">
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => handleSaveCaption(image.id, image.caption)}
                                disabled={galleryBusy || !canManageGallery}
                              >
                                Save caption
                              </button>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => handleSetPrimaryImage(image.id)}
                                disabled={galleryBusy || !canManageGallery || image.isPrimary}
                              >
                                {image.isPrimary ? 'Primary image' : 'Set as primary'}
                              </button>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => handleDeleteImage(image.id)}
                                disabled={galleryBusy || !canManageGallery}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="venue-details-field-hint">
                      No gallery photos uploaded yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="venue-details-general-field venue-details-general-field--full">
              <label className="venue-details-field-label">
                <span className="label-text">Venue description</span>
                <span className="required-asterisk">*</span>
              </label>
              <textarea
                className="venue-details-input venue-details-textarea"
                value={venueDetails?.description || ''}
                onChange={(e) => setVenueDetails?.({ ...venueDetails, description: e.target.value })}
                rows={4}
                placeholder="Describe your venue"
                readOnly={!setVenueDetails}
              />
            </div>
            <div className="venue-details-general-row">
              <div className="venue-details-general-field">
                <label className="venue-details-field-label">
                  <span className="label-text">Seating capacity</span>
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="number"
                  className="venue-details-input"
                  value={venueDetails?.seating_capacity ?? venueDetails?.max_capacity ?? ''}
                  onChange={(e) => setVenueDetails?.({ ...venueDetails, seating_capacity: e.target.value ? parseInt(e.target.value, 10) : '', max_capacity: e.target.value ? parseInt(e.target.value, 10) : venueDetails?.max_capacity })}
                  placeholder="0"
                  min={0}
                  readOnly={!setVenueDetails}
                />
              </div>
              <div className="venue-details-general-field">
                <label className="venue-details-field-label">
                  <span className="label-text">Standing capacity</span>
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="number"
                  className="venue-details-input"
                  value={venueDetails?.standing_capacity ?? ''}
                  onChange={(e) => setVenueDetails?.({ ...venueDetails, standing_capacity: e.target.value ? parseInt(e.target.value, 10) : '' })}
                  placeholder="0"
                  min={0}
                  readOnly={!setVenueDetails}
                />
              </div>
            </div>
            <div className="venue-details-toggle-section venue-details-toggle-section--row">
              <div className="venue-details-toggle-block">
                <label className="venue-details-field-label">
                  <span className="label-text">Alcohol</span>
                  <span className="required-asterisk">*</span>
                </label>
                <div className="venue-details-toggle-row">
                  <label className="venue-details-toggle-item">
                    <span className="venue-details-toggle-label">Alcohol provided by the venue</span>
                    <span className="switch">
                      <input
                        type="checkbox"
                        checked={venueDetails?.serves_alcohol ?? venueDetails?.amenities?.food_beverage?.alcohol?.venueProvides ?? false}
                        onChange={(e) => setVenueDetails?.({ ...venueDetails, serves_alcohol: e.target.checked })}
                        disabled={!setVenueDetails}
                      />
                      <span className="slider" />
                    </span>
                  </label>
                  <label className="venue-details-toggle-item">
                    <span className="venue-details-toggle-label">BYO alcohol allowed</span>
                    <span className="switch">
                      <input
                        type="checkbox"
                        checked={venueDetails?.outside_alcohol_allowed ?? venueDetails?.amenities?.food_beverage?.alcohol?.byoAllowed ?? false}
                        onChange={(e) => setVenueDetails?.({ ...venueDetails, outside_alcohol_allowed: e.target.checked })}
                        disabled={!setVenueDetails}
                      />
                      <span className="slider" />
                    </span>
                  </label>
                  <label className="venue-details-toggle-item">
                    <span className="venue-details-toggle-label">Corkage fee for BYO alcohol</span>
                    <span className="switch">
                      <input
                        type="checkbox"
                        checked={
                          venueDetails?.corkage_fee_for_byo ??
                          venueDetails?.amenities?.food_beverage?.alcohol?.corkageFee ??
                          false
                        }
                        onChange={(e) => setVenueDetails?.({ ...venueDetails, corkage_fee_for_byo: e.target.checked })}
                        disabled={!setVenueDetails}
                      />
                      <span className="slider" />
                    </span>
                  </label>
                </div>
              </div>
              <div className="venue-details-toggle-block">
                <label className="venue-details-field-label">
                  <span className="label-text">Catering</span>
                  <span className="required-asterisk">*</span>
                </label>
                <div className="venue-details-toggle-row">
                  <label className="venue-details-toggle-item">
                    <span className="venue-details-toggle-label">Provides in-house catering</span>
                    <span className="switch">
                      <input
                        type="checkbox"
                        checked={venueDetails?.in_house_catering ?? (Array.isArray(venueDetails?.amenities?.food_beverage?.inHouseCatering) && venueDetails?.amenities?.food_beverage?.inHouseCatering?.length > 0) ?? false}
                        onChange={(e) => setVenueDetails?.({ ...venueDetails, in_house_catering: e.target.checked })}
                        disabled={!setVenueDetails}
                      />
                      <span className="slider" />
                    </span>
                  </label>
                  <label className="venue-details-toggle-item">
                    <span className="venue-details-toggle-label">External catering allowed</span>
                    <span className="switch">
                      <input
                        type="checkbox"
                        checked={venueDetails?.external_catering_allowed ?? (Array.isArray(venueDetails?.amenities?.food_beverage?.externalCatering) && venueDetails?.amenities?.food_beverage?.externalCatering?.length > 0) ?? false}
                        onChange={(e) => setVenueDetails?.({ ...venueDetails, external_catering_allowed: e.target.checked })}
                        disabled={!setVenueDetails}
                      />
                      <span className="slider" />
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div className="venue-details-public-info-group">
              <div className="venue-details-public-info-header">
                <h4>Public venue info shown to organizers</h4>
                <p>
                  These fields power the public Venue Details sections: Food and beverage, Event rules, Amenities, and
                  Cancellation policy.
                </p>
              </div>
              <div className="venue-details-general-row">
                <div className="venue-details-general-field venue-details-general-field--full">
                  <label className="venue-details-field-label">
                    <span className="label-text">In-house catering options</span>
                  </label>
                  <textarea
                    className="venue-details-input venue-details-textarea"
                    rows={4}
                    value={
                      venueDetails?.in_house_catering_options_text ??
                      toMultilineText(
                        venueDetails?.amenities?.food_beverage?.inHouseCatering ??
                        venueDetails?.amenities?.foodBeverage?.inHouseCatering ??
                        [],
                        (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
                      )
                    }
                    onChange={(e) =>
                      setVenueDetails?.({ ...venueDetails, in_house_catering_options_text: e.target.value })
                    }
                    placeholder={'One option per line'}
                    readOnly={!setVenueDetails}
                  />
                </div>
                <div className="venue-details-general-field venue-details-general-field--full">
                  <label className="venue-details-field-label">
                    <span className="label-text">External catering options</span>
                  </label>
                  <textarea
                    className="venue-details-input venue-details-textarea"
                    rows={4}
                    value={
                      venueDetails?.external_catering_options_text ??
                      toMultilineText(
                        venueDetails?.amenities?.food_beverage?.externalCatering ??
                        venueDetails?.amenities?.foodBeverage?.externalCatering ??
                        [],
                        (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
                      )
                    }
                    onChange={(e) =>
                      setVenueDetails?.({ ...venueDetails, external_catering_options_text: e.target.value })
                    }
                    placeholder={'One option per line'}
                    readOnly={!setVenueDetails}
                  />
                </div>
              </div>
              <div className="venue-details-general-row">
                <div className="venue-details-general-field venue-details-general-field--full">
                  <label className="venue-details-field-label">
                    <span className="label-text">Allowed events</span>
                  </label>
                  <textarea
                    className="venue-details-input venue-details-textarea"
                    rows={4}
                    value={
                      venueDetails?.allowed_events_text ??
                      toMultilineText(
                        venueDetails?.amenities?.event_rules?.allowedEvents ??
                        venueDetails?.amenities?.eventRules?.allowedEvents ??
                        [],
                        (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
                      )
                    }
                    onChange={(e) =>
                      setVenueDetails?.({ ...venueDetails, allowed_events_text: e.target.value })
                    }
                    placeholder={'One event type per line'}
                    readOnly={!setVenueDetails}
                  />
                </div>
                <div className="venue-details-general-field venue-details-general-field--full">
                  <label className="venue-details-field-label">
                    <span className="label-text">Booking page host rules</span>
                  </label>
                  <p className="venue-details-field-help">
                    Shown to organizers on the booking page under &ldquo;Review host rules&rdquo;, and on your public
                    venue details page.
                  </p>
                  <textarea
                    className="venue-details-input venue-details-textarea"
                    rows={4}
                    value={
                      venueDetails?.host_rules_text ??
                      toMultilineText(
                        venueDetails?.amenities?.event_rules?.hostRules ??
                        venueDetails?.amenities?.event_rules?.host_rules ??
                        venueDetails?.amenities?.eventRules?.hostRules ??
                        [],
                        (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
                      )
                    }
                    onChange={(e) =>
                      setVenueDetails?.({ ...venueDetails, host_rules_text: e.target.value })
                    }
                    placeholder={DEFAULT_BOOKING_HOST_RULES_TEXT}
                    readOnly={!setVenueDetails}
                  />
                </div>
              </div>
              <div className="venue-details-general-row">
                <div className="venue-details-general-field venue-details-general-field--full">
                  <label className="venue-details-field-label">
                    <span className="label-text">Amenities list</span>
                  </label>
                  <textarea
                    className="venue-details-input venue-details-textarea"
                    rows={4}
                    value={
                      venueDetails?.amenities_text ??
                      toMultilineText(
                        venueDetails?.amenities?.amenities ?? venueDetails?.facilities ?? [],
                        (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
                      )
                    }
                    onChange={(e) =>
                      setVenueDetails?.({ ...venueDetails, amenities_text: e.target.value })
                    }
                    placeholder={'One amenity per line'}
                    readOnly={!setVenueDetails}
                  />
                </div>
              </div>
              <div className="venue-details-general-row">
                <div className="venue-details-general-field venue-details-general-field--full">
                  <label className="venue-details-field-label">
                    <span className="label-text">Cancellation policy</span>
                  </label>
                  <textarea
                    className="venue-details-input venue-details-textarea"
                    rows={4}
                    value={
                      venueDetails?.cancellation_policy_text ??
                      venueDetails?.amenities?.event_rules?.cancellationPolicy ??
                      venueDetails?.amenities?.eventRules?.cancellationPolicy ??
                      venueDetails?.cancellation_policy ??
                      ''
                    }
                    onChange={(e) =>
                      setVenueDetails?.({ ...venueDetails, cancellation_policy_text: e.target.value })
                    }
                    placeholder="Describe cancellation policy"
                    readOnly={!setVenueDetails}
                  />
                </div>
              </div>
              <div className="venue-details-general-row">
                <div className="venue-details-general-field venue-details-general-field--full">
                  <label className="venue-details-field-label">
                    <span className="label-text">Payment link</span>
                  </label>
                  <input
                    type="url"
                    className="venue-details-input"
                    value={venueDetails?.payment_link || ''}
                    onChange={(e) =>
                      setVenueDetails?.({ ...venueDetails, payment_link: e.target.value })
                    }
                    placeholder="https://your-bank-transfer-or-stripe-link.com/..."
                    readOnly={!setVenueDetails}
                  />
                  <p className="venue-details-field-hint">
                    Used in booking emails until Occasia payments are fully integrated. Leave blank to use the Occasia booking payment page.
                  </p>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {activeTab === 'add-ons' && (
        <div className="venue-details-tab-content">
          <div className="section-title-row">
            <h4>Items</h4>
          </div>
          {itemsError && <div className="inline-error">{itemsError}</div>}
          <div className="addons-table">
            <div className="table-header addons-header">
              <div className="table-cell">Item</div>
              <div className="table-cell">Price per unit</div>
              <div className="table-cell table-cell-action"></div>
            </div>
            {normalizedAddOnItems.map((item, index) => {
              const itemId = item.id || item.item_id;
              if (!itemId) return null;
              return (
              <div className="table-row addons-row addons-row--with-tags" key={itemId || `${item.name}-${index}`}>
                <div className="table-cell">
                  <input
                    className="form-input"
                    value={getAddOnItemField(item, 'name')}
                    onChange={(event) => updateExistingAddOnItem(itemId, 'name', event.target.value)}
                    aria-label={`Name for ${getAddOnItemField(item, 'name')}`}
                  />
                </div>
                <div className="table-cell">
                  <input
                    className="form-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={getAddOnItemField(item, 'price')}
                    onChange={(event) => updateExistingAddOnItem(itemId, 'price', event.target.value)}
                    aria-label={`Price for ${getAddOnItemField(item, 'name')}`}
                  />
                </div>
                <div className="table-cell table-cell-action">
                  <button
                    className="delete-btn"
                    onClick={() => handleAddOnDelete(itemId, index)}
                    aria-label={`Delete ${getAddOnItemField(item, 'name')}`}
                    type="button"
                  >
                    <FigmaTrashIcon className="icon-trash" />
                  </button>
                </div>
                <div className="venue-details-tags-row">
                  <span className="venue-details-tags-label">Tags</span>
                  <CatalogTagPicker
                    catalogTags={catalogTags}
                    value={item.tags}
                    onChange={(nextTags) => updateExistingAddOnItem(itemId, 'tags', nextTags)}
                    ariaLabel={`Tags for ${getAddOnItemField(item, 'name')}`}
                  />
                </div>
              </div>
              );
            })}
            {draftAddOnItems.map((draft, index) => (
              <div className="table-row addons-row addons-row--with-tags" key={`draft-${index}`}>
                <div className="table-cell">
                  <input
                    className="form-input"
                    placeholder="Enter item name"
                    value={draft.name}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraftAddOnItems(items => items.map((item, idx) => (
                        idx === index ? { ...item, name: value } : item
                      )));
                    }}
                  />
                </div>
                <div className="table-cell">
                  <input
                    className="form-input"
                    placeholder="Enter price per unit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={draft.price}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraftAddOnItems(items => items.map((item, idx) => (
                        idx === index ? { ...item, price: value } : item
                      )));
                    }}
                  />
                </div>
                <div className="table-cell table-cell-action">
                  <button
                    className="delete-btn"
                    type="button"
                    onClick={() => removeDraftRow(index)}
                    aria-label="Remove draft item"
                  >
                    <FigmaTrashIcon className="icon-trash" />
                  </button>
                </div>
                <div className="venue-details-tags-row">
                  <span className="venue-details-tags-label">Tags</span>
                  <CatalogTagPicker
                    catalogTags={catalogTags}
                    value={draft.tags}
                    onChange={(nextTags) => {
                      setDraftAddOnItems((items) => items.map((item, idx) => (
                        idx === index ? { ...item, tags: nextTags } : item
                      )));
                    }}
                    ariaLabel={`Tags for ${draft.name || 'new item'}`}
                  />
                </div>
              </div>
            ))}
            <div className="addons-add-row">
              <button
                className="add-btn"
                onClick={() => {
                  setDraftAddOnItems(items => [...items, { name: '', price: '', tags: [] }]);
                }}
                disabled={isSavingItem}
              >
                <FigmaAddSquareIcon className="icon-add-square" />
                Add another
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'others' && (
        <div className="venue-details-tab-content">
          <div className="section-title-row">
            <h4>Other items</h4>
            <p className="venue-details-field-hint">
              Standalone items priced per number, per hour, or per event. These appear on the booking page items list.
            </p>
          </div>
          {itemsError && <div className="inline-error">{itemsError}</div>}
          <div className="addons-table others-table">
            <div className="table-header others-header">
              <div className="table-cell">Item</div>
              <div className="table-cell">Price</div>
              <div className="table-cell">Unit</div>
              <div className="table-cell">Default qty</div>
              <div className="table-cell table-cell-action"></div>
            </div>
            {normalizedOtherItems.map((item, index) => {
              const itemId = item.id || item.item_id;
              if (!itemId) return null;
              const pricingUnit = getOtherItemField(item, 'pricing_unit');
              return (
                <div className="table-row others-row others-row--with-tags" key={itemId || `${item.name}-${index}`}>
                  <div className="table-cell">
                    <input
                      className="form-input"
                      value={getOtherItemField(item, 'name')}
                      onChange={(event) => updateExistingOtherItem(itemId, 'name', event.target.value)}
                      aria-label={`Name for ${getOtherItemField(item, 'name')}`}
                    />
                  </div>
                  <div className="table-cell">
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={getOtherItemField(item, 'price')}
                      onChange={(event) => updateExistingOtherItem(itemId, 'price', event.target.value)}
                      aria-label={`Price for ${getOtherItemField(item, 'name')}`}
                    />
                  </div>
                  <div className="table-cell">
                    <select
                      className="form-input"
                      value={pricingUnit}
                      onChange={(event) => updateExistingOtherItem(itemId, 'pricing_unit', event.target.value)}
                      aria-label={`Pricing unit for ${getOtherItemField(item, 'name')}`}
                    >
                      {OTHER_PRICING_UNIT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="table-cell">
                    <input
                      className="form-input"
                      type="number"
                      min="1"
                      step="1"
                      disabled={pricingUnit === ADDON_PRICING_UNITS.PER_EVENT}
                      value={pricingUnit === ADDON_PRICING_UNITS.PER_EVENT ? 1 : getOtherItemField(item, 'default_quantity')}
                      onChange={(event) => updateExistingOtherItem(itemId, 'default_quantity', event.target.value)}
                      aria-label={`Default quantity for ${getOtherItemField(item, 'name')}`}
                    />
                  </div>
                  <div className="table-cell table-cell-action">
                    <button
                      className="delete-btn"
                      onClick={() => handleOtherDelete(itemId, index)}
                      aria-label={`Delete ${getOtherItemField(item, 'name')}`}
                      type="button"
                    >
                      <FigmaTrashIcon className="icon-trash" />
                    </button>
                  </div>
                  <div className="venue-details-tags-row">
                    <span className="venue-details-tags-label">Tags</span>
                    <CatalogTagPicker
                      catalogTags={catalogTags}
                      value={item.tags}
                      onChange={(nextTags) => updateExistingOtherItem(itemId, 'tags', nextTags)}
                      ariaLabel={`Tags for ${getOtherItemField(item, 'name')}`}
                    />
                  </div>
                </div>
              );
            })}
            {draftOtherItems.map((draft, index) => {
              const pricingUnit = draft.pricing_unit || ADDON_PRICING_UNITS.PER_NUMBER;
              return (
                <div className="table-row others-row others-row--with-tags" key={`other-draft-${index}`}>
                  <div className="table-cell">
                    <input
                      className="form-input"
                      placeholder="Enter item name"
                      value={draft.name}
                      onChange={(event) => {
                        const value = event.target.value;
                        setDraftOtherItems((items) => items.map((item, idx) => (
                          idx === index ? { ...item, name: value } : item
                        )));
                      }}
                    />
                  </div>
                  <div className="table-cell">
                    <input
                      className="form-input"
                      placeholder="Enter price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={draft.price}
                      onChange={(event) => {
                        const value = event.target.value;
                        setDraftOtherItems((items) => items.map((item, idx) => (
                          idx === index ? { ...item, price: value } : item
                        )));
                      }}
                    />
                  </div>
                  <div className="table-cell">
                    <select
                      className="form-input"
                      value={pricingUnit}
                      onChange={(event) => {
                        const value = event.target.value;
                        setDraftOtherItems((items) => items.map((item, idx) => (
                          idx === index
                            ? {
                                ...item,
                                pricing_unit: value,
                                default_quantity: value === ADDON_PRICING_UNITS.PER_EVENT ? 1 : (item.default_quantity || 1)
                              }
                            : item
                        )));
                      }}
                    >
                      {OTHER_PRICING_UNIT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="table-cell">
                    <input
                      className="form-input"
                      type="number"
                      min="1"
                      step="1"
                      disabled={pricingUnit === ADDON_PRICING_UNITS.PER_EVENT}
                      value={pricingUnit === ADDON_PRICING_UNITS.PER_EVENT ? 1 : (draft.default_quantity ?? 1)}
                      onChange={(event) => {
                        const value = event.target.value;
                        setDraftOtherItems((items) => items.map((item, idx) => (
                          idx === index ? { ...item, default_quantity: value } : item
                        )));
                      }}
                    />
                  </div>
                  <div className="table-cell table-cell-action">
                    <button
                      className="delete-btn"
                      type="button"
                      onClick={() => removeOtherDraftRow(index)}
                      aria-label="Remove draft item"
                    >
                      <FigmaTrashIcon className="icon-trash" />
                    </button>
                  </div>
                  <div className="venue-details-tags-row">
                    <span className="venue-details-tags-label">Tags</span>
                    <CatalogTagPicker
                      catalogTags={catalogTags}
                      value={draft.tags}
                      onChange={(nextTags) => {
                        setDraftOtherItems((items) => items.map((item, idx) => (
                          idx === index ? { ...item, tags: nextTags } : item
                        )));
                      }}
                      ariaLabel={`Tags for ${draft.name || 'new other item'}`}
                    />
                  </div>
                </div>
              );
            })}
            <div className="addons-add-row others-add-row">
              <button
                className="add-btn"
                onClick={() => {
                  setDraftOtherItems((items) => [
                    ...items,
                    {
                      name: '',
                      price: '',
                      pricing_unit: ADDON_PRICING_UNITS.PER_NUMBER,
                      default_quantity: 1,
                      tags: [],
                    }
                  ]);
                }}
                disabled={isSavingItem}
              >
                <FigmaAddSquareIcon className="icon-add-square" />
                Add another
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'packages' && (
        <div className="venue-details-tab-content">
          <div className="section-title-row packages-header-row">
            <h4>Packages</h4>
            <button
              className="link-button add-package-button"
              type="button"
              onClick={openPackageModalForCreate}
            >
              <FigmaAddIcon className="icon-add" />
              Create package
            </button>
          </div>
          {packagesError && <div className="inline-error">{packagesError}</div>}
          <div className="packages-grid">
            {normalizedPackages.length === 0 && (
              <div className="empty-state">No packages created yet.</div>
            )}
            {normalizedPackages.map(pkg => (
              <div className="package-card" key={pkg.id || pkg.name}>
                <div className="package-card-header">
                  <div>
                    <h5>{pkg.name}</h5>
                    <p>{getPackageSummary(pkg)}</p>
                    <p className="package-price">{currencySymbol}{getPackageTotalPrice(pkg)}</p>
                  </div>
                  <div className="package-card-actions">
                    <button
                      className="link-button package-edit-button"
                      type="button"
                      onClick={() => openPackageModalForEdit(pkg)}
                    >
                      Edit
                    </button>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={pkg.isActive}
                        onChange={() => handlePackageToggle(pkg)}
                      />
                      <span className="slider"></span>
                    </label>
                    <button
                      className="delete-btn"
                      type="button"
                      aria-label={`Delete ${pkg.name}`}
                      onClick={() => handlePackageDelete(pkg.id)}
                    >
                      <FigmaTrashIcon className="icon-trash" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {showPackageModal && (
            <div className="modal-overlay" role="dialog" aria-modal="true">
              <div className="modal-card">
                <div className="modal-header">
                  <h4>{editingPackageId ? 'Edit package' : 'Create package'}</h4>
                  <button
                    className="modal-close"
                    type="button"
                    onClick={closePackageModal}
                  >
                    <FigmaCloseModalIcon className="icon-close" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-section">
                    <label className="form-label">Package name *</label>
                    <input
                      className="form-input"
                      value={packageForm.name}
                      onChange={(event) => setPackageForm(prev => ({
                        ...prev,
                        name: event.target.value
                      }))}
                      placeholder="Super bundle"
                    />
                  </div>
                  <div className="form-section">
                    <label className="form-label">Tags</label>
                    <CatalogTagPicker
                      catalogTags={catalogTags}
                      value={packageForm.tags}
                      onChange={(nextTags) => setPackageForm((prev) => ({ ...prev, tags: nextTags }))}
                      ariaLabel="Package tags"
                    />
                  </div>
                  <div className="form-section">
                    <label className="form-label">Available for *</label>
                    <div className="checkbox-row">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={packageForm.availableFor.tableBooking}
                          onChange={() => setPackageForm(prev => ({
                            ...prev,
                            availableFor: {
                              ...prev.availableFor,
                              tableBooking: !prev.availableFor.tableBooking
                            }
                          }))}
                        />
                        Per person booking
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={packageForm.availableFor.fullVenueHire}
                          onChange={() => setPackageForm(prev => ({
                            ...prev,
                            availableFor: {
                              ...prev.availableFor,
                              fullVenueHire: !prev.availableFor.fullVenueHire
                            }
                          }))}
                        />
                        Full venue hire
                      </label>
                    </div>
                  </div>
                  <div className="form-section">
                    <label className="form-label">Items *</label>
                    <div className="select-field">
                      <button
                        className="select-trigger"
                        type="button"
                        onClick={() => setShowItemsDropdown(prev => !prev)}
                      >
                        Select
                        <FigmaChevronDownIcon className="icon-chevron" />
                      </button>
                      {showItemsDropdown && (
                        <div className="select-dropdown">
                          {normalizedAddOnItems.length === 0 && (
                            <div className="dropdown-empty">No items available</div>
                          )}
                        {normalizedAddOnItems.map(item => {
                          const itemKey = item.id || item.item_id;
                          const isSelected = Boolean(packageForm.items[itemKey]);
                            return (
                            <label className="dropdown-item" key={itemKey || item.name}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => togglePackageItem(item)}
                                />
                                <span>{item.name}</span>
                                <span className="dropdown-price">{currencySymbol}{item.price}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {selectedPackageItems.length > 0 && (
                      <div className="selected-package-items">
                        {selectedPackageItems.map(item => {
                          const itemId = item.itemId || item.id || item.item_id;
                          const qty = Math.max(1, getItemQuantity(item));
                          return (
                            <div className="selected-package-item-row" key={itemId || item.name}>
                              <span className="selected-item-name">{item.name}</span>
                              <input
                                type="number"
                                min={1}
                                value={qty}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value, 10);
                                  updatePackageItemQuantity(itemId, Number.isNaN(val) || val < 1 ? 1 : val);
                                }}
                                className="package-item-quantity-input"
                                aria-label={`Quantity for ${item.name}`}
                              />
                              <button
                                type="button"
                                className="selected-tag-remove"
                                onClick={() => togglePackageItem(item)}
                                aria-label={`Remove ${item.name}`}
                              >
                                <FigmaCloseTagIcon className="icon-tag-close" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  {packagesError ? <div className="inline-error">{packagesError}</div> : null}
                  <div className="package-total">
                    <label className="package-total-label" htmlFor="package-custom-price">
                      Package price
                    </label>
                    <div className="package-price-input-row">
                      <span className="package-price-prefix">{currencySymbol}</span>
                      <input
                        id="package-custom-price"
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-input package-price-input"
                        value={packageForm.customPrice}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          setPackageForm((prev) => ({
                            ...prev,
                            customPrice: value,
                            priceManuallyEdited: true
                          }));
                        }}
                        aria-label="Package price"
                      />
                    </div>
                    {packageForm.priceManuallyEdited &&
                    calculatedPackagePrice > 0 &&
                    Math.abs(resolvedPackagePrice - calculatedPackagePrice) > 0.02 ? (
                      <span className="package-price-hint">
                        Calculated from items: {currencySymbol}
                        {Number(calculatedPackagePrice.toFixed(2))}
                        {' · '}
                        <button
                          type="button"
                          className="link-button package-price-reset"
                          onClick={() =>
                            setPackageForm((prev) => ({
                              ...prev,
                              customPrice:
                                calculatedPackagePrice > 0
                                  ? String(Number(calculatedPackagePrice.toFixed(2)))
                                  : '',
                              priceManuallyEdited: false
                            }))
                          }
                        >
                          Use calculated price
                        </button>
                      </span>
                    ) : (
                      <span className="package-price-hint">
                        Auto-calculated from selected items
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={(event) => {
                      event.preventDefault();
                      handleSavePackage();
                    }}
                    disabled={!canSavePackage || isSavingPackage}
                  >
                    {isSavingPackage
                      ? 'Saving...'
                      : editingPackageId
                        ? 'Save changes'
                        : 'Save package'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VenueDetailsForm;

